import DatePicker from "@components/Admin/DonationsTab/DatePickerCalendar";
import { Card } from "@components/Card";
import { ErrorMessage } from "@components/ErrorMessage";
import { Modal } from "@components/Modal";
import { PopoverInfoButton } from "@components/PopoverInfoButton";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useCallback, useEffect, useState } from "react";

import {
  FundraiserResponse,
  NonEmptyMatchingCampaignResponse,
} from "@every.org/common/src/codecs/entities";
import { SafeInt } from "@every.org/common/src/codecs/number";
import { colorPalette } from "@every.org/common/src/display/palette";
import { Currency } from "@every.org/common/src/entity/types";
import {
  setDateToTheEndOfTheDayPST,
  setDateToTheStartOfTheDayPST,
  setDateToTheUsersTimezoneWithoutHours,
  todayAtTheStartOfTheDay,
} from "@every.org/common/src/helpers/date";
import {
  getDonationMatchCampaignRouteSpec,
  updateDonationMatchCampaignRouteSpec,
} from "@every.org/common/src/routes/campaign";

import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "src/components/Button";
import { TextInputLabelText } from "src/components/InputContainer";
import { InputWithSideDescriptionContainer } from "src/components/Nonprofit/EditForm/shared";
import { AmountInput } from "src/pages/Fundraiser/EditForm/AmoutInput";
import FundraiserValidationError from "src/pages/Fundraiser/EditForm/FundraiserValidationError";
import { InputWithLabelContainer } from "src/pages/Fundraiser/EditForm/styles";
import {
  FundraiserEditFormKind,
  FundraiserValidationErrors,
} from "src/pages/Fundraiser/EditForm/types";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight, TextSize, textSizeCss } from "src/theme/text";
import { queryApi } from "src/utility/apiClient";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";

const StyledAmountInput = styled(AmountInput)`
  max-width: 380px;
  ${cssForMediaSize({
    max: MediaSize.LARGE,
    css: css`
      max-width: 100%;
    `,
  })};
`;

export interface FundraiserMatchCampaignEdit {
  maxTotalMatchAmount: SafeInt | undefined;
  currentTotalMatchAmount: SafeInt | undefined;
  maxMatchPerDonationOneTimeAmount: SafeInt | undefined;
  dateRange: [Date | null, Date | null] | null[] | Date[];
  active: boolean;
  id?: string;
  deleted: boolean;
}

export const matchCampaignInfoCheck = ({
  fundraiser,
  hasMatchingCampaign,
  matchCampaign,
  isResumeError = false,
}: {
  fundraiser?: FundraiserResponse;
  hasMatchingCampaign: boolean;
  matchCampaign?: FundraiserMatchCampaignEdit;
  isResumeError?: boolean;
}) => {
  const today = todayAtTheStartOfTheDay();
  if (hasMatchingCampaign) {
    if (
      !matchCampaign?.maxTotalMatchAmount ||
      (matchCampaign.maxMatchPerDonationOneTimeAmount &&
        matchCampaign.maxMatchPerDonationOneTimeAmount >
          matchCampaign?.maxTotalMatchAmount) ||
      (matchCampaign.currentTotalMatchAmount &&
        matchCampaign.currentTotalMatchAmount >=
          matchCampaign?.maxTotalMatchAmount) ||
      (matchCampaign.dateRange[1] !== null &&
        matchCampaign.dateRange[1] < today)
    ) {
      const validation = createValidation({
        ...fundraiser,
        matchCampaign,
      });
      const validationMessage = Object.values(validation).filter(
        (val) => val !== undefined && !val.success
      )[0]?.message;
      const message = `${
        isResumeError
          ? "Match campaign cannot be resumed"
          : "Fundraiser cannot be saved"
      } with this condition${
        validationMessage ? `: ${validationMessage}` : ""
      }`;
      throw new FundraiserValidationError({
        message,
        validation,
      });
    }
    return;
  }
};

function createValidation(params: {
  matchCampaign?: FundraiserMatchCampaignEdit;
}) {
  const date = new Date();
  return {
    matchCampaign: !params.matchCampaign
      ? {
          success: false,
          message: "A match campaign has not been fully defined yet",
        }
      : undefined,
    maxTotalMatchAmount: !params?.matchCampaign?.maxTotalMatchAmount
      ? {
          success: false,
          message: "Please specify the total matching funds",
        }
      : params.matchCampaign.maxMatchPerDonationOneTimeAmount &&
        params.matchCampaign.maxTotalMatchAmount &&
        params.matchCampaign.maxMatchPerDonationOneTimeAmount >
          params.matchCampaign.maxTotalMatchAmount
      ? {
          success: false,
          message:
            "Max per donation amount should be less than or equal to the total match amount",
        }
      : params.matchCampaign.maxTotalMatchAmount &&
        params.matchCampaign.currentTotalMatchAmount &&
        params.matchCampaign.currentTotalMatchAmount >=
          params.matchCampaign.maxTotalMatchAmount
      ? {
          success: false,
          message:
            "Total matching funds must be greater than the amount already matched",
        }
      : undefined,
    dateRange:
      params.matchCampaign &&
      params.matchCampaign.dateRange[1] !== null &&
      params.matchCampaign.dateRange[1] < date
        ? {
            success: false,
            message: "End date must be a future date",
          }
        : undefined,
  };
}

export const MatchCampaignCard = ({
  matchCampaignEdit,
  setMatchCampaignEdit,
  fundraiser,
  setIsMatchingCampaignModalOpen,
  kind,
  setShowAdvanced,
  setHasMatchingCampaign,
  setSubmitError,
  setValidationErrors,
  validationErrors,
}: {
  matchCampaignEdit: FundraiserMatchCampaignEdit;
  setMatchCampaignEdit: React.Dispatch<
    React.SetStateAction<FundraiserMatchCampaignEdit>
  >;
  setIsMatchingCampaignModalOpen: (value: boolean) => void;
  fundraiser?: FundraiserResponse;
  kind: FundraiserEditFormKind;
  setShowAdvanced: (value: boolean) => void;
  setHasMatchingCampaign: (value: boolean) => void;
  setSubmitError: (value: React.SetStateAction<string | undefined>) => void;
  validationErrors?: FundraiserValidationErrors;
  setValidationErrors: (
    value: React.SetStateAction<FundraiserValidationErrors | undefined>
  ) => void;
}) => {
  const [matchCampaign, setMatchCampaign] = useState<
    NonEmptyMatchingCampaignResponse | undefined
  >(undefined);
  const [
    isActiveMatchingCampaignModalOpen,
    setIsActiveMatchingCampaignModalOpen,
  ] = useState(false);
  const [datePickerDateRange, setDatePickerDateRange] = useState<
    [Date | null, Date | null] | Date[] | null[]
  >(
    matchCampaignEdit.dateRange.map((date, i) =>
      date === null
        ? null
        : setDateToTheUsersTimezoneWithoutHours({
            date,
            startOfTheDay: i === 0,
          })
    ) as [Date | null, Date | null] | Date[] | null[]
  );
  const [matchCampaignError, setMatchCampaignError] = useState<
    string | undefined
  >(undefined);

  const now = new Date();
  const isEnded =
    matchCampaign &&
    ((matchCampaign.endDate && now >= new Date(matchCampaign.endDate)) ||
      !matchCampaign.active ||
      !(matchCampaign.maxTotalMatchAmount > matchCampaign.matchedTotal));
  const showDeleteFlow = !matchCampaign || matchCampaign?.matchedTotal === 0;

  const fetchFundraiserMatchCampaign = useCallback(async () => {
    if (
      kind === FundraiserEditFormKind.EDIT &&
      fundraiser?.matchCampaigns &&
      fundraiser.matchCampaigns[0]
    ) {
      try {
        const response = await queryApi(getDonationMatchCampaignRouteSpec, {
          routeTokens: {
            identifier: fundraiser.matchCampaigns[0]?.id,
          },
          body: {},
          queryParams: { skipCache: true },
        });
        if (Object.keys(response).length > 0 && "id" in response) {
          setMatchCampaign(response);
          setDatePickerDateRange([
            response.startDate
              ? setDateToTheUsersTimezoneWithoutHours({
                  date: new Date(response.startDate),
                  startOfTheDay: true,
                })
              : null,
            response.endDate
              ? setDateToTheUsersTimezoneWithoutHours({
                  date: new Date(response.endDate),
                  startOfTheDay: false,
                })
              : null,
          ]);
          setEditableMatchCampaignByKey({
            id: response.id,
            dateRange: [
              response.startDate ? new Date(response.startDate) : null,
              response.endDate ? new Date(response.endDate) : null,
            ],
            maxTotalMatchAmount: response.maxTotalMatchAmount,
            maxMatchPerDonationOneTimeAmount:
              response.maxMatchPerDonationOneTimeAmount || undefined,
            active: response.active,
            currentTotalMatchAmount: response.matchedTotal,
            deleted: response.deleted,
          });
          setShowAdvanced(true);
          setHasMatchingCampaign(true);
        }
      } catch {
        setMatchCampaign(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundraiser?.matchCampaigns, kind]);

  useEffect(() => {
    fetchFundraiserMatchCampaign();
  }, [fetchFundraiserMatchCampaign]);

  const setEditableMatchCampaignByKey = useCallback(
    (payload: Partial<FundraiserMatchCampaignEdit>) => {
      setMatchCampaignEdit((matchCampaignEdit) => {
        return {
          ...matchCampaignEdit,
          ...payload,
        };
      });
    },
    [setMatchCampaignEdit]
  );

  const updateFundraiserMatchCampaign = useCallback(
    async (matchCampaign: FundraiserMatchCampaignEdit) => {
      setValidationErrors(undefined);
      setSubmitError(undefined);
      if (matchCampaignEdit.id) {
        try {
          matchCampaignInfoCheck({
            hasMatchingCampaign: true,
            fundraiser,
            matchCampaign: matchCampaignEdit,
          });
          const response = await queryApi(
            updateDonationMatchCampaignRouteSpec,
            {
              routeTokens: {
                identifier: matchCampaignEdit.id,
              },
              body: isEnded
                ? {
                    maxMatchPerDonationOneTimeAmount:
                      matchCampaignEdit.maxMatchPerDonationOneTimeAmount,
                    maxTotalMatchAmount: matchCampaignEdit.maxTotalMatchAmount,
                    startDate: matchCampaignEdit.dateRange[0],
                    endDate: matchCampaignEdit.dateRange[1],
                    active: true,
                  }
                : {
                    maxMatchPerDonationOneTimeAmount:
                      matchCampaignEdit.maxMatchPerDonationOneTimeAmount,
                    maxTotalMatchAmount: matchCampaignEdit.maxTotalMatchAmount,
                    startDate: matchCampaign.dateRange[0],
                    endDate: matchCampaignEdit.dateRange[1],
                    active: false,
                  },
              queryParams: {},
            }
          );

          if (Object.keys(response).length > 0 && "id" in response) {
            setMatchCampaign(response);
            setEditableMatchCampaignByKey({
              id: response.id,
              dateRange: [
                response.startDate ? new Date(response.startDate) : null,
                response.endDate ? new Date(response.endDate) : null,
              ],
              maxTotalMatchAmount: response.maxTotalMatchAmount,
              maxMatchPerDonationOneTimeAmount:
                response.maxMatchPerDonationOneTimeAmount || undefined,
              active: response.active,
              currentTotalMatchAmount: response.matchedTotal,
            });
          }
          setIsActiveMatchingCampaignModalOpen(false);
        } catch (e) {
          if (e instanceof FundraiserValidationError) {
            setValidationErrors(e.validation);
          }
          if (e instanceof Error) {
            setSubmitError(e.message);
          }
          setIsActiveMatchingCampaignModalOpen(false);
        }
      }
    },
    [
      matchCampaignEdit,
      setEditableMatchCampaignByKey,
      setSubmitError,
      setValidationErrors,
      fundraiser,
      isEnded,
    ]
  );

  const activeMatchingCampaignModal = (
    <Modal
      isOpen={isActiveMatchingCampaignModalOpen}
      onRequestClose={() => setIsActiveMatchingCampaignModalOpen(false)}
    >
      <div css={[verticalStackCss.s, { maxWidth: "400px" }]}>
        <h2
          css={css`
            ${textSizeCss[TextSize.m]}
          `}
        >
          {isEnded ? "Resume match?" : "End your match?"}
        </h2>
        {isEnded ? (
          <p>Donations will start being matched again.</p>
        ) : (
          <p>
            Donors will still be able to contribute to the fundraiser, but their
            donations will not be matched.
          </p>
        )}
        <div
          css={[
            css`
              display: flex;
              flex-direction: column-reverse;
              gap: ${spacing.l};
              margin-top: ${spacing.m};
            `,
            cssForMediaSize({
              min: MediaSize.MEDIUM_SMALL,
              css: css`
                flex-direction: row;
                justify-content: right;
              `,
            }),
          ]}
        >
          <Button
            data-tname="MatchingCampaignModal_cancel"
            role={ButtonRole.TEXT_ONLY}
            onClick={{
              kind: ButtonTargetKind.FUNCTION,
              action: () => setIsActiveMatchingCampaignModalOpen(false),
            }}
          >
            Cancel
          </Button>
          <Button
            data-tname="MatchingCampaignModal_submit"
            data-bloom-block
            role={ButtonRole.PRIMARY}
            onClick={{
              kind: ButtonTargetKind.FUNCTION,
              action: () => {
                updateFundraiserMatchCampaign(matchCampaignEdit);
              },
            }}
          >
            {isEnded ? "Resume match" : "End match"}
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <Card>
      <div css={verticalStackCss.xl}>
        <h3>
          Matching campaign{" "}
          {isEnded && (
            <span
              css={css`
                font-weight: ${FontWeight.REGULAR};
                ${textSizeCss[TextSize.m]}
              `}
            >
              - Ended{" "}
              {matchCampaign.endDate &&
              new Date() >= new Date(matchCampaign.endDate)
                ? new Date(matchCampaign.endDate).toLocaleDateString()
                : matchCampaign.matchedTotal >=
                  matchCampaign.maxTotalMatchAmount
                ? "(ran out of funds)"
                : ""}
            </span>
          )}
        </h3>
        <div css={verticalStackCss.l}>
          <InputWithSideDescriptionContainer>
            <div
              css={css`
                ${horizontalStackCss.xs}
                ${cssForMediaSize({
                  max: MediaSize.LARGE,
                  css: css`
                    ${verticalStackCss.xs}
                  `,
                })};
              `}
            >
              <InputWithLabelContainer>
                <TextInputLabelText htmlFor="maxTotalMatchAmountInput">
                  Total matching funds
                </TextInputLabelText>
                <StyledAmountInput
                  amountValue={matchCampaignEdit.maxTotalMatchAmount}
                  setAmountValue={(e) => {
                    setValidationErrors((prev) => ({
                      ...prev,
                      maxTotalMatchAmount: undefined,
                    }));
                    setEditableMatchCampaignByKey({
                      maxTotalMatchAmount: e,
                    });
                  }}
                  data-tname="maxTotalMatch"
                  id="maxTotalMatchInput"
                  name="maxTotalMatchAmountInput"
                  currency={fundraiser?.raisedData?.currency || Currency.USD}
                  validationErrors={
                    validationErrors?.maxTotalMatchAmount?.message
                  }
                />
              </InputWithLabelContainer>
              <InputWithLabelContainer>
                <TextInputLabelText htmlFor="maxMatchPerDonationOneTimeAmountInput">
                  Maximum match per donation
                </TextInputLabelText>
                <StyledAmountInput
                  amountValue={
                    matchCampaignEdit.maxMatchPerDonationOneTimeAmount
                  }
                  setAmountValue={(e) => {
                    setValidationErrors((prev) => ({
                      ...prev,
                      maxMatchPerDonationOneTimeAmount: undefined,
                    }));
                    setEditableMatchCampaignByKey({
                      maxMatchPerDonationOneTimeAmount: e,
                    });
                  }}
                  data-tname="maxMatchPerDonationOneTime"
                  id="maxMatchPerDonationOneTimeInput"
                  name="maxMatchPerDonationOneTimeAmountInput"
                  currency={fundraiser?.raisedData?.currency || Currency.USD}
                  validationErrors={
                    validationErrors?.maxMatchPerDonationOneTimeAmount?.message
                  }
                />
              </InputWithLabelContainer>
            </div>
            <span
              css={css`
                padding-top: ${spacing.l};
              `}
            >
              If you have matching funds for donations we can display each
              donor&apos;s matched amount when they make their donation. The
              match will automatically end if all matching funds have been used
              or if an end date is reached. Every.org does not provide the
              matching funds.
            </span>
          </InputWithSideDescriptionContainer>
          <div css={verticalStackCss.xxl}>
            <InputWithLabelContainer>
              <TextInputLabelText
                css={[horizontalStackCss.xxs, { alignItems: "center" }]}
              >
                <span>Start and end date</span>
                <PopoverInfoButton text="All dates begin at 12:00am PST/PDT and end at 11:59pm PST/PDT" />
              </TextInputLabelText>
              <InputWithSideDescriptionContainer>
                <DatePicker
                  inputCss={[
                    textSizeCss.s,
                    css`
                      max-width: 200px;
                    `,
                  ]}
                  setDateRange={(date) => {
                    setValidationErrors((prev) => ({
                      ...prev,
                      dateRange: undefined,
                    }));
                    setDatePickerDateRange(date);
                    const adjustedDate = Array.isArray(date)
                      ? date.map((d, i) => {
                          if (d === null) {
                            return d;
                          }
                          if (i === 0) {
                            return setDateToTheStartOfTheDayPST(d);
                          }
                          if (i === 1) {
                            return setDateToTheEndOfTheDayPST(d);
                          }
                          return d;
                        })
                      : date;
                    setEditableMatchCampaignByKey({
                      dateRange: adjustedDate as
                        | [Date | null, Date | null]
                        | null[]
                        | Date[],
                    });
                  }}
                  dateRange={datePickerDateRange}
                  contentCss={css`
                    height: 64px;
                    padding: ${spacing.m};
                  `}
                  placeholder="Select dates"
                  validationErrors={validationErrors?.dateRange?.message}
                />
                <span>All donations will be matched 1:1</span>
              </InputWithSideDescriptionContainer>
            </InputWithLabelContainer>
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                ${cssForMediaSize({
                  max: MediaSize.LARGE,
                  css: css`
                    ${verticalStackCss.s}
                    justify-content: center;
                  `,
                })};
              `}
            >
              <div
                css={css`
                  border-radius: 16px;
                  border: 1px solid ${colorPalette.grayLight};
                  max-width: 380px;
                  width: 380px;
                  padding: ${spacing.m};
                  ${cssForMediaSize({
                    max: MediaSize.LARGE,
                    css: css`
                      width: 100%;
                    `,
                  })};
                `}
              >
                <h4>Match summary</h4>
                <ul
                  css={css`
                    li {
                      display: flex;
                      justify-content: space-between;
                      padding: 8px 0 8px 0;
                      border-bottom: 1px solid ${colorPalette.grayLight};
                    }
                    li:last-child {
                      border-bottom: none;
                    }
                  `}
                >
                  <li>
                    <p>Donated</p>
                    <span>
                      {displayCurrencyValueInUserLocale({
                        minDenomCurrencyValue: {
                          currency:
                            fundraiser?.raisedData?.currency || Currency.USD,
                          amountInMinDenom:
                            fundraiser?.raisedData?.raised || (0 as SafeInt),
                        },
                      })}
                    </span>
                  </li>
                  <li>
                    <p>Matched</p>
                    <span>
                      {displayCurrencyValueInUserLocale({
                        minDenomCurrencyValue: {
                          currency: matchCampaign?.currency || Currency.USD,
                          amountInMinDenom:
                            matchCampaign?.matchedTotal || (0 as SafeInt),
                        },
                      })}
                    </span>
                  </li>
                  <li>
                    <p>Total raised</p>
                    <span>
                      {displayCurrencyValueInUserLocale({
                        minDenomCurrencyValue: {
                          currency: matchCampaign?.currency || Currency.USD,
                          amountInMinDenom: (fundraiser?.raisedData?.raised &&
                          matchCampaign?.matchedTotal
                            ? fundraiser?.raisedData.raised +
                              matchCampaign.matchedTotal
                            : 0) as SafeInt,
                        },
                      })}
                    </span>
                  </li>
                  <li>
                    <p>Remaining match</p>
                    <span>
                      {displayCurrencyValueInUserLocale({
                        minDenomCurrencyValue: {
                          currency: matchCampaign?.currency || Currency.USD,
                          amountInMinDenom:
                            (matchCampaign?.maxTotalMatchAmount &&
                            matchCampaign?.matchedTotal
                              ? matchCampaign?.maxTotalMatchAmount -
                                matchCampaign?.matchedTotal
                              : matchCampaign?.maxTotalMatchAmount
                              ? matchCampaign?.maxTotalMatchAmount
                              : 0) as SafeInt,
                        },
                      })}
                    </span>
                  </li>
                </ul>
              </div>
              <Button
                data-tname="endCampaign-MatchSettings"
                role={ButtonRole.SECONDARY}
                size={ButtonSize.LARGE}
                css={[
                  css`
                    align-self: last baseline;
                    ${cssForMediaSize({
                      max: MediaSize.LARGE,
                      css: css`
                        align-self: center;
                      `,
                    })};
                  `,
                  showDeleteFlow && { borderColor: colorPalette.red },
                ]}
                contentCss={showDeleteFlow && { color: colorPalette.red }}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: () => {
                    setMatchCampaignError(undefined);
                    if (!showDeleteFlow && isEnded) {
                      try {
                        matchCampaignInfoCheck({
                          hasMatchingCampaign: true,
                          fundraiser,
                          matchCampaign: matchCampaignEdit,
                          isResumeError: true,
                        });
                      } catch (e) {
                        if (e instanceof FundraiserValidationError) {
                          setValidationErrors(e.validation);
                        }
                        if (e instanceof Error) {
                          setMatchCampaignError(e.message);
                        }
                        return;
                      }
                    }
                    !showDeleteFlow
                      ? setIsActiveMatchingCampaignModalOpen(true)
                      : setIsMatchingCampaignModalOpen(true);
                  },
                }}
              >
                {showDeleteFlow
                  ? "Delete match"
                  : isEnded
                  ? "Resume match"
                  : "End match"}
              </Button>
            </div>
            {matchCampaignError && (
              <ErrorMessage>{matchCampaignError}</ErrorMessage>
            )}
          </div>
        </div>
      </div>
      {activeMatchingCampaignModal}
    </Card>
  );
};
